import type { ParagraphFormat, SceneElement, TextElement, TextMark } from "./schema";

/**
 * Mixed formatting and lists in text elements (docs/04-dokumentformat.md).
 *
 * `text` stays the plain source of truth (search, MCP, captions of templates).
 * `marks` format parts of it by offset, `paragraphs` formats its lines. Both
 * are optional: a text without them renders exactly as before. The editor
 * writes normalized values (sorted, no overlaps, no empty ranges); readers
 * still normalize, because other writers (MCP, older clients) may change
 * `text` without touching the marks.
 */

/** Fields a mark can override. `colorToken` travels with `color`. */
export const MARK_FIELDS = [
  "fontFamily",
  "fontSize",
  "fontWeight",
  "italic",
  "underline",
  "strike",
  "color",
] as const;
export type MarkField = (typeof MARK_FIELDS)[number];

export type MarkStyle = Omit<TextMark, "from" | "to">;

export interface TextRun {
  /** Offset of the run in `text`. */
  from: number;
  text: string;
  style: MarkStyle;
}

export interface TextParagraph {
  /** Offset of the paragraph's first character in `text`. */
  from: number;
  text: string;
  format: ParagraphFormat | null;
  /** List marker ("•", "3.") or null. */
  marker: string | null;
  runs: TextRun[];
}

function markStyle(mark: TextMark): MarkStyle {
  const style: MarkStyle = {};
  for (const field of MARK_FIELDS) {
    if (mark[field] !== undefined) (style as Record<string, unknown>)[field] = mark[field];
  }
  if (style.color !== undefined && mark.colorToken) style.colorToken = mark.colorToken;
  // Underline and line-through can only be added.
  if (style.underline === false) delete style.underline;
  if (style.strike === false) delete style.strike;
  return style;
}

function hasStyle(style: MarkStyle): boolean {
  return MARK_FIELDS.some((field) => style[field] !== undefined);
}

export function sameMarkStyle(a: MarkStyle, b: MarkStyle): boolean {
  return (
    MARK_FIELDS.every((field) => a[field] === b[field]) &&
    (a.colorToken ?? null) === (b.colorToken ?? null)
  );
}

/**
 * Sorted, non-overlapping marks inside `[0, length)`, adjacent equal ranges
 * joined. Where marks overlap, later ones win field by field.
 */
export function normalizeMarks(marks: readonly TextMark[] | undefined, length: number): TextMark[] {
  if (!marks || marks.length === 0 || length <= 0) return [];
  const clamped = marks
    .map((mark) => ({
      from: Math.max(0, Math.min(length, mark.from)),
      to: Math.max(0, Math.min(length, mark.to)),
      style: markStyle(mark),
    }))
    .filter((mark) => mark.to > mark.from && hasStyle(mark.style));
  if (clamped.length === 0) return [];

  const sorted = clamped.every((mark, index) => index === 0 || clamped[index - 1]!.to <= mark.from);
  let segments: { from: number; to: number; style: MarkStyle }[];
  if (sorted) {
    segments = clamped;
  } else {
    // Overlaps: split at every boundary and merge the styles covering each piece.
    const cuts = [...new Set(clamped.flatMap((mark) => [mark.from, mark.to]))].sort(
      (a, b) => a - b,
    );
    segments = [];
    for (let index = 0; index < cuts.length - 1; index++) {
      const from = cuts[index]!;
      const to = cuts[index + 1]!;
      let style: MarkStyle | null = null;
      for (const mark of clamped) {
        if (mark.from <= from && mark.to >= to) style = { ...(style ?? {}), ...mark.style };
      }
      if (style) segments.push({ from, to, style });
    }
  }

  const out: TextMark[] = [];
  for (const segment of segments) {
    const last = out[out.length - 1];
    if (last && last.to === segment.from && sameMarkStyle(markStyle(last), segment.style)) {
      last.to = segment.to;
    } else {
      out.push({ from: segment.from, to: segment.to, ...segment.style });
    }
  }
  return out;
}

/** Paragraph formats for `count` paragraphs; undefined when all are plain. */
export function normalizeParagraphs(
  paragraphs: readonly (ParagraphFormat | null | undefined)[] | undefined,
  count: number,
): (ParagraphFormat | null)[] | undefined {
  if (!paragraphs) return undefined;
  const out: (ParagraphFormat | null)[] = [];
  for (let index = 0; index < Math.min(count, paragraphs.length); index++) {
    const format = paragraphs[index];
    const next: ParagraphFormat = {};
    if (format?.list) next.list = format.list;
    if (format?.indent) next.indent = format.indent;
    out.push(next.list || next.indent ? next : null);
  }
  while (out.length > 0 && out[out.length - 1] === null) out.pop();
  return out.length > 0 ? out : undefined;
}

/** True when the element uses marks or paragraph formats. */
export function isRichText(element: Pick<TextElement, "marks" | "paragraphs">): boolean {
  return (
    (element.marks?.length ?? 0) > 0 ||
    (element.paragraphs ?? []).some((format) => Boolean(format?.list || format?.indent))
  );
}

const BULLETS = ["•", "◦", "▪"];

function alpha(value: number): string {
  let out = "";
  let rest = value;
  while (rest > 0) {
    rest -= 1;
    out = String.fromCharCode(97 + (rest % 26)) + out;
    rest = Math.floor(rest / 26);
  }
  return out;
}

function roman(value: number): string {
  const table: [number, string][] = [
    [1000, "m"],
    [900, "cm"],
    [500, "d"],
    [400, "cd"],
    [100, "c"],
    [90, "xc"],
    [50, "l"],
    [40, "xl"],
    [10, "x"],
    [9, "ix"],
    [5, "v"],
    [4, "iv"],
    [1, "i"],
  ];
  let out = "";
  let rest = value;
  for (const [amount, digits] of table) {
    while (rest >= amount) {
      out += digits;
      rest -= amount;
    }
  }
  return out;
}

function numberLabel(level: number, value: number): string {
  const kind = level % 3;
  return `${kind === 0 ? value : kind === 1 ? alpha(value) : roman(value)}.`;
}

/**
 * List markers per paragraph. Numbered items count per nesting level; a
 * paragraph without list ends all lists, a shallower item ends deeper ones.
 */
export function listMarkers(
  formats: readonly (ParagraphFormat | null | undefined)[],
): (string | null)[] {
  const counters = [0, 0, 0, 0, 0];
  const kinds: (string | undefined)[] = [];
  return formats.map((format) => {
    if (!format?.list) {
      counters.fill(0);
      kinds.length = 0;
      return null;
    }
    const level = Math.max(0, Math.min(4, format.indent ?? 0));
    for (let deeper = level + 1; deeper < counters.length; deeper++) {
      counters[deeper] = 0;
      kinds[deeper] = undefined;
    }
    if (format.list === "number") {
      counters[level] = kinds[level] === "number" ? counters[level]! + 1 : 1;
      kinds[level] = "number";
      return numberLabel(level, counters[level]!);
    }
    counters[level] = 0;
    kinds[level] = "bullet";
    return BULLETS[level % BULLETS.length]!;
  });
}

/** The text split into paragraphs and runs of equal formatting. */
export function textParagraphs(
  element: Pick<TextElement, "text" | "marks" | "paragraphs">,
): TextParagraph[] {
  const lines = element.text.split("\n");
  const marks = normalizeMarks(element.marks, element.text.length);
  const formats = normalizeParagraphs(element.paragraphs, lines.length) ?? [];
  const markers = listMarkers(lines.map((_, index) => formats[index] ?? null));
  const out: TextParagraph[] = [];
  let offset = 0;
  let markIndex = 0;
  lines.forEach((line, index) => {
    const from = offset;
    const to = from + line.length;
    const runs: TextRun[] = [];
    let cursor = from;
    while (markIndex < marks.length && marks[markIndex]!.to <= from) markIndex++;
    for (let scan = markIndex; scan < marks.length && cursor < to; scan++) {
      const mark = marks[scan]!;
      if (mark.from >= to) break;
      const start = Math.max(mark.from, from);
      const end = Math.min(mark.to, to);
      if (start > cursor) {
        runs.push({ from: cursor, text: element.text.slice(cursor, start), style: {} });
      }
      if (end > start) {
        runs.push({ from: start, text: element.text.slice(start, end), style: markStyle(mark) });
      }
      cursor = Math.max(cursor, end);
    }
    if (cursor < to) runs.push({ from: cursor, text: element.text.slice(cursor, to), style: {} });
    out.push({
      from,
      text: line,
      format: formats[index] ?? null,
      marker: markers[index] ?? null,
      runs,
    });
    offset = to + 1;
  });
  return out;
}

/** Removes fields from all marks (the element's own value applies to the whole text). */
export function clearMarkFields(element: TextElement, fields: readonly MarkField[]): void {
  if (!element.marks) return;
  const marks = element.marks.map((mark) => {
    const next = { ...mark };
    for (const field of fields) delete next[field];
    if (fields.includes("color")) delete next.colorToken;
    return next;
  });
  const normalized = normalizeMarks(marks, element.text.length);
  if (normalized.length > 0) element.marks = normalized;
  else delete element.marks;
}

/** Scales font sizes of marks (resizing, format transfer). */
export function scaleTextMarks(element: TextElement, factor: number): void {
  if (!element.marks) return;
  for (const mark of element.marks) {
    if (mark.fontSize !== undefined) mark.fontSize = Math.max(1, mark.fontSize * factor);
  }
}

/**
 * Replaces the text. Marks and paragraph formats only fit the old text, so
 * they are dropped when it changes (MCP, pasting over a text).
 */
export function setPlainText(element: TextElement, text: string): void {
  if (element.text === text) return;
  element.text = text;
  delete element.marks;
  delete element.paragraphs;
}

export interface TextFace {
  family: string;
  weight: number;
  italic: boolean;
  /** Characters set in this face (font subsetting, loading). */
  text: string;
}

/** Font faces a text element uses, with the characters of each. */
export function textFaces(element: TextElement): TextFace[] {
  const { style } = element;
  const faces = new Map<string, TextFace>();
  const add = (family: string, weight: number, italic: boolean, text: string) => {
    const key = `${family}|${weight}|${italic}`;
    const value = style.uppercase ? text.toUpperCase() : text;
    const face = faces.get(key);
    if (face) face.text += value;
    else faces.set(key, { family, weight, italic, text: value });
  };
  if (!isRichText(element)) {
    add(style.fontFamily, style.fontWeight, style.italic, element.text);
    return [...faces.values()];
  }
  for (const paragraph of textParagraphs(element)) {
    if (paragraph.marker) add(style.fontFamily, style.fontWeight, style.italic, paragraph.marker);
    for (const run of paragraph.runs) {
      add(
        run.style.fontFamily ?? style.fontFamily,
        run.style.fontWeight ?? style.fontWeight,
        run.style.italic ?? style.italic,
        run.text,
      );
    }
  }
  if (faces.size === 0) add(style.fontFamily, style.fontWeight, style.italic, "");
  return [...faces.values()];
}

/** Font families of a text element (the element's own first). */
export function textFamilies(element: TextElement): string[] {
  const families = new Set([element.style.fontFamily]);
  for (const mark of element.marks ?? []) if (mark.fontFamily) families.add(mark.fontFamily);
  return [...families];
}

/** Text colours in use (the element's own first). */
export function textColors(element: TextElement): string[] {
  const colors = new Set([element.style.color]);
  for (const mark of normalizeMarks(element.marks, element.text.length)) {
    if (mark.color) colors.add(mark.color);
  }
  return [...colors];
}

/** Smallest font size in use. */
export function smallestFontSize(element: TextElement): number {
  let size = element.style.fontSize;
  for (const mark of normalizeMarks(element.marks, element.text.length)) {
    if (mark.fontSize !== undefined) size = Math.min(size, mark.fontSize);
  }
  return size;
}

/** Font families used by texts, captions and charts of a document. */
export function documentFontFamilies(elements: Record<string, SceneElement>): string[] {
  const families = new Set<string>();
  for (const element of Object.values(elements)) {
    if (element.type === "text") for (const family of textFamilies(element)) families.add(family);
    else if (element.type === "captions") families.add(element.style.fontFamily);
    else if (element.type === "chart") families.add(element.fontFamily);
    else if (element.type === "table") families.add(element.fontFamily);
  }
  return [...families];
}

/**
 * After a whole-document replace (MCP): texts whose `text` changed while their
 * marks and paragraph formats stayed as they were lose them, since the old
 * offsets no longer fit.
 */
export function dropStaleRichText(
  before: Record<string, SceneElement>,
  after: Record<string, SceneElement>,
): void {
  for (const [id, element] of Object.entries(after)) {
    const previous = before[id];
    if (element.type !== "text" || previous?.type !== "text" || previous.text === element.text) {
      continue;
    }
    if (JSON.stringify(previous.marks ?? null) === JSON.stringify(element.marks ?? null)) {
      delete element.marks;
    }
    // Paragraph formats still fit while the number of lines stays.
    const lines = (text: string) => text.split("\n").length;
    if (
      JSON.stringify(previous.paragraphs ?? null) === JSON.stringify(element.paragraphs ?? null) &&
      lines(previous.text) !== lines(element.text)
    ) {
      delete element.paragraphs;
    }
  }
}
